/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { DateTime } from 'luxon'
import { useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormField from '../../../../client/js/components/form/fields/FormField'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../client/js/components/RecordMetas'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import { fetchIntervention } from '../../lib/rest/interventionAPIClient'
import { patchTransaction, postTransaction } from '../../lib/rest/transactionAPIClient'
import TransactionSchema from '../../schemas/TransactionSchema.mjs'
import CustomerField from '../fields/CustomerField'
import EstateField from '../fields/EstateField'
import InterventionField from '../fields/InterventionField'
import LeaseField from '../fields/LeaseField'
import PriceField from '../fields/PriceField'
import FormSection from '../FormSection'
import useTransactionCategoryOptions from './useTransactionCategoryOptions'
import { useQuery } from '@tanstack/react-query'
import { randomId } from '../../lib/random.mjs'
import { parseBoolean } from '@jalik/form-parser'
import TransactionFileField from '../fields/TransactionFileField'
import { queryClient } from '../../../../client/js/lib/query'
import { getTransactionListLink } from '../../lib/links/transactionLinks'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import { Alert, Checkbox, Select, SimpleGrid, Stack } from '@mantine/core'
import {
  CATEGORY_CHARGE,
  CATEGORY_RENT,
  CATEGORY_RENTAL_CHARGE
} from '../../enums/TransactionCategories.mjs'
import LockedInput from '../inputs/LockedInput'
import FormDateField from '../../../../client/js/components/form/fields/FormDateField'
import FormDateTimeField from '../../../../client/js/components/form/fields/FormDatetimeField'
import { capitalize } from 'lodash'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle'
import PriceInput from '../inputs/PriceInput'
import { FieldError } from '@jalik/schema'

export type TransactionFormProps = {
  initialValues: Partial<Transaction>
}

function TransactionForm (props: TransactionFormProps) {
  const intl = useIntl()
  const { initialValues } = props
  const { hasPermission } = useContext(AuthContext)
  const editable = hasPermission('write:transaction', 'write:invoice')
  const userCanUnlock = hasPermission('unlock:transaction')
  const [locked, setLocked] = useState<boolean>(initialValues?.paid || false)
  const id = initialValues?.id

  const startOfMonth = useMemo(() => (
    DateTime.now().startOf('month')
  ), [])

  const validateDueAt = (dueAt?: string) => {
    // todo expense only or revenue also ?
    if (id == null && !values.paid && typeof dueAt === 'string' && DateTime.fromISO(dueAt) < startOfMonth) {
      const error = new FieldError('dueAt', 'dueAt')
      error.message = intl.formatMessage({
        id: 'cannot_add_operation_on_before_current_month',
        defaultMessage: 'Impossible de saisir une opération non payée avant le mois en cours.'
      })
      return error
    }
  }

  const form = useCustomForm({
    disabled: !editable,
    initialValues: useMemo<Partial<Transaction>>(() => ({
      paid: false,
      tmpRef: randomId(),
      ...initialValues
    }), [initialValues]),
    initializeField: createFieldInitializer(TransactionSchema),
    reinitialize: true,
    validate: async (values) => {
      const errors = (await createFormValidator(TransactionSchema)(values)) ?? {}
      errors.dueAt = validateDueAt(values.dueAt) ?? errors.dueAt
      return errors
    },
    validateField: async (name, value, values) => {
      const error = await createFieldValidator(TransactionSchema)(name, value, values)
      if (name === 'dueAt' && !error && typeof value === 'string') {
        return validateDueAt(value)
      }
      return error
    },
    onSubmit: (submitValues: Partial<Transaction>) => {
      const finalValues = removeUnknownFields(submitValues, TransactionSchema)
      return id
        ? patchTransaction(id, finalValues)
        : postTransaction(finalValues)
    },
    onSuccess () {
      queryClient.invalidateQueries({ queryKey: ['transaction', id] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
    transform (mutation, values) {
      const mut = { ...mutation }

      if (values.expense) {
        if (typeof mut.amount === 'number') {
          mut.amount = Math.abs(mut.amount) * -1
        }
        if (typeof mut.vat === 'number') {
          mut.vat = Math.abs(mut.vat) * -1
        }
        if (typeof mut.cps === 'number') {
          mut.cps = Math.abs(mut.cps) * -1
        }
      }
      return mut
    }
  })
  const { setValue, values } = form

  // Get intervention.
  const interventionQuery = useQuery({
    enabled: values.interventionId != null,
    queryKey: ['intervention', values.interventionId],
    queryFn: () => fetchIntervention(values.interventionId),
    refetchOnWindowFocus: false
  })
  const { data: intervention } = interventionQuery

  // Estate filter.
  const estateFilter = useMemo(() => ({
    customerId: values.customerId
  }), [values.customerId])

  // Lease filter.
  const leaseFilter = useMemo(() => ({
    customerId: values.customerId,
    estateId: values.estateId
  }), [values.customerId, values.estateId])

  // Intervention filter.
  const interventionFilter = useMemo(() => ({
    leaseId: values.leaseId,
    estateId: values.estateId,
    missingTransaction: true
  }), [values.estateId, values.leaseId])

  // Set label from intervention.
  useEffect(() => {
    if (intervention != null) {
      setValue('label', intervention.subject)
    }
  }, [intervention, setValue])

  const categoryOptions = useTransactionCategoryOptions()

  const total = useMemo(() => (
    [values.amount, values.vat, values.cps]
      .filter((v) => typeof v === 'number')
      .reduce((p, c) => p + c, 0)
  ), [values.amount, values.cps, values.vat])

  const isDisabled = useMemo(() => (
    initialValues.paid || (initialValues.generated && initialValues.id != null)
  ), [initialValues.generated, initialValues.id, initialValues.paid])

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? getTransactionListLink(values.expense) : undefined}
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        {initialValues.generated
          ? <Alert>
            <FormattedMessage
              id="transaction_generated"
              defaultMessage="Cette transaction a été générée par le système et ne peut être modifiée."
            />
          </Alert>
          : null}

        <SimpleGrid cols={{ lg: 2 }}>
          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'general',
              defaultMessage: 'Général'
            })}
          >
            <CustomerField
              disabled={isDisabled || initialValues.customerId != null}
            />
            <EstateField
              disabled={isDisabled ||
                values.customerId == null ||
                initialValues.estateId != null}
              queryFilter={estateFilter}
            />
            <LeaseField
              disabled={isDisabled ||
                values.estateId == null ||
                initialValues.leaseId != null}
              queryFilter={leaseFilter}
            />
            <InterventionField
              disabled={values.estateId == null}
              queryFilter={interventionFilter}
            />
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'dates',
              defaultMessage: 'Dates'
            })}
          >
            <FormDateField
              description={intl.formatMessage({
                id: 'transaction_due_date_desc',
                defaultMessage: 'Date après laquelle la transaction est considérée comme impayée.'
              })}
              disabled={locked ||
                (initialValues.dueAt != null && initialValues.paid && locked) ||
                values.invoiceId != null}
              // todo use lease start date as min value
              min={values.expense && initialValues.dueAt == null && !values.paid
                ? startOfMonth.toISODate()
                : null}
              name="dueAt"
              label={intl.formatMessage({
                id: 'due_date',
                defaultMessage: 'Échéance'
              })}
            />

            {values.expense &&
            !values.paid &&
            values.leaseId &&
            !values.invoiceId &&
            values.dueAt &&
            DateTime.fromISO(values.dueAt).hasSame(DateTime.now(), 'month')
              ? <Alert color="yellow" icon={<FontAwesomeIcon icon={faExclamationTriangle} />}>
                {intl.formatMessage({
                  id: 'expense_due_at_will_be_injected_in_next_invoice',
                  defaultMessage: 'La dépense sera injectée dans la prochaine facture.'
                })}
              </Alert>
              : null}

            <FormDateTimeField
              disabled={!values.paid}
              name="paidAt"
              label={intl.formatMessage({
                id: 'paid_at',
                defaultMessage: 'Payée le'
              })}
              hideTime
            />
            <FormField
              disabled={locked}
              name="paid"
              label={intl.formatMessage({
                id: 'paid',
                defaultMessage: 'Payé'
              })}
              parser={parseBoolean}
              render={Checkbox}
              type="checkbox"
              value="true"
            />

            {values.expense &&
            values.paid &&
            values.paid !== initialValues.paid &&
            [CATEGORY_CHARGE, CATEGORY_RENTAL_CHARGE, CATEGORY_RENT].includes(values.category)
              ? <Alert>
                {intl.formatMessage({
                  id: 'a_revenue_will_be_created_for_this_expense',
                  defaultMessage: 'Un revenu correspondant à cette dépense sera automatiquement créé (ex: loyer ou charge payée par le locataire = revenu pour le propriétaire).'
                })}
              </Alert>
              : null}
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'description',
              defaultMessage: 'Description'
            })}
          >
            <FormField
              disabled={isDisabled || typeof initialValues.expense === 'boolean'}
              name="expense"
              label={intl.formatMessage({
                id: 'type',
                defaultMessage: 'Type'
              })}
              parser={parseBoolean}
              render={Select}
              data={[
                {
                  value: 'true',
                  label: intl.formatMessage({
                    id: 'expense',
                    defaultMessage: 'Dépense'
                  })
                },
                {
                  value: 'false',
                  label: intl.formatMessage({
                    id: 'revenue',
                    defaultMessage: 'Revenu'
                  })
                }
              ]}
            />
            <FormField
              disabled={isDisabled}
              name="label"
              label={intl.formatMessage({
                id: 'label',
                defaultMessage: 'Libellé'
              })}
              parser={capitalize}
            />
            <FormField
              disabled={isDisabled}
              name="category"
              label={intl.formatMessage({
                id: 'category',
                defaultMessage: 'Catégorie'
              })}
              render={Select}
              data={categoryOptions}
            />
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'amounts',
              defaultMessage: 'Montants'
            })}
          >
            <PriceField
              disabled={isDisabled}
              min={values.expense ? undefined : 0}
              max={values.expense ? 0 : undefined}
              name="amount"
              label={intl.formatMessage({
                id: 'amount_excluding_tax',
                defaultMessage: 'Montant HT'
              })}
            />
            <PriceField
              disabled={isDisabled}
              min={values.expense ? undefined : 0}
              max={values.expense ? 0 : undefined}
              name="vat"
              label={intl.formatMessage({
                id: 'vat',
                defaultMessage: 'TVA'
              })}
            />
            <PriceField
              disabled={isDisabled}
              min={values.expense ? undefined : 0}
              max={values.expense ? 0 : undefined}
              name="cps"
              label={intl.formatMessage({
                id: 'cps',
                defaultMessage: 'CPS'
              })}
            />
            <PriceInput
              disabled
              id="total"
              label={intl.formatMessage({
                id: 'total_including_tax',
                defaultMessage: 'Total TTC'
              })}
              value={total}
              thousandSeparator=" "
            />
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'file',
              defaultMessage: 'Fichier'
            })}
          >
            <TransactionFileField
              tmpRef={values.tmpRef}
              transactionId={id}
            />
          </FormSection>
        </SimpleGrid>

        {editable
          ? <ButtonGroup>
            {userCanUnlock && initialValues?.paid
              ? <LockedInput
                onChange={setLocked}
                value={locked}
              />
              : null}

            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default TransactionForm
